/**
 * Client-side key management for zero-knowledge encryption
 *
 * Architecture:
 * 1. Device Key Pair (X25519) - Generated on first login, stored in IndexedDB
 * 2. Workspace Seed (symmetric) - Random 32 bytes, wrapped with device key via X25519-AES-KEM
 * 3. Epoch Keys - Derived from seed using HKDF with time-based epochs
 * 4. Epoch = 7 days (automatic key rotation)
 */

import type {
  KeyEpoch,
  WorkspaceSeed,
  WorkspaceEpochKey,
  DeviceKeyPair,
  EncryptedDocument,
  EncryptedUpdate,
} from '@yada/types';

// Epoch duration: 7 days in seconds
const EPOCH_DURATION = 7 * 24 * 60 * 60;

/**
 * Get current time-based key epoch
 */
export function getCurrentEpoch(): KeyEpoch {
  const now = Math.floor(Date.now() / 1000);
  const epoch = Math.floor(now / EPOCH_DURATION);
  const startTimestamp = epoch * EPOCH_DURATION;
  const endTimestamp = (epoch + 1) * EPOCH_DURATION;

  return {
    epoch,
    startDate: new Date(startTimestamp * 1000).toISOString(),
    endDate: new Date(endTimestamp * 1000).toISOString(),
  };
}

/**
 * Get epoch for a specific timestamp
 */
export function getEpochForTimestamp(timestamp: number): KeyEpoch {
  const epoch = Math.floor(timestamp / (EPOCH_DURATION * 1000));
  const startTimestamp = epoch * EPOCH_DURATION;
  const endTimestamp = (epoch + 1) * EPOCH_DURATION;

  return {
    epoch,
    startDate: new Date(startTimestamp * 1000).toISOString(),
    endDate: new Date(endTimestamp * 1000).toISOString(),
  };
}

/**
 * Generate device key pair (X25519 for wrapping/KEM)
 */
export async function generateDeviceKey(deviceId: string): Promise<DeviceKeyPair> {
  const keyPair = await crypto.subtle.generateKey(
    {
      name: 'X25519',
    },
    false, // non-extractable: private key cannot be extracted via JavaScript
    ['deriveKey', 'deriveBits']
  ) as CryptoKeyPair;

  return {
    deviceId,
    publicKey: keyPair.publicKey,
    privateKey: keyPair.privateKey,
    algorithm: 'X25519',
    createdAt: new Date().toISOString(),
  };
}

/**
 * Generate workspace seed (random 32 bytes)
 */
export async function generateWorkspaceSeed(workspaceId: string): Promise<WorkspaceSeed> {
  const seed = crypto.getRandomValues(new Uint8Array(32));

  return {
    workspaceId,
    seed: seed.buffer,
    algorithm: 'HKDF-SHA256',
    createdAt: new Date().toISOString(),
  };
}

/**
 * Wrap workspace seed with device public key using X25519 KEM + AES-GCM
 * 
 * Payload structure (concatenated ArrayBuffer):
 * [32 bytes: Ephemeral Public Key] + [12 bytes: IV] + [Encrypted Data + 16 byte Tag]
 */
export async function wrapSeed(
  seed: ArrayBuffer,
  devicePublicKey: CryptoKey
): Promise<ArrayBuffer> {
  // 1. Check if it's an RSA key (legacy support for wrapping)
  if (devicePublicKey.algorithm.name === 'RSA-OAEP') {
    return await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, devicePublicKey, seed);
  }

  // 2. X25519 KEM
  // Generate ephemeral key pair
  const ephemeral = await crypto.subtle.generateKey({ name: 'X25519' }, true, ['deriveKey']) as CryptoKeyPair;
  
  // Derive shared secret via ECDH
  const sharedSecret = await crypto.subtle.deriveKey(
    { name: 'X25519', public: devicePublicKey },
    ephemeral.privateKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  );

  // Encrypt the seed with AES-GCM
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encryptedSeed = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    sharedSecret,
    seed
  );

  // Export ephemeral public key (raw 32 bytes)
  const ephemeralPubRaw = await crypto.subtle.exportKey('raw', ephemeral.publicKey);

  // Concatenate: Pub(32) + IV(12) + Data(N)
  const result = new Uint8Array(32 + 12 + encryptedSeed.byteLength);
  result.set(new Uint8Array(ephemeralPubRaw), 0);
  result.set(iv, 32);
  result.set(new Uint8Array(encryptedSeed), 44);

  return result.buffer;
}

/**
 * Unwrap workspace seed with device private key
 */
export async function unwrapSeed(
  wrappedSeed: ArrayBuffer,
  devicePrivateKey: CryptoKey
): Promise<ArrayBuffer> {
  // 1. Legacy RSA support
  if (devicePrivateKey.algorithm.name === 'RSA-OAEP') {
    return await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, devicePrivateKey, wrappedSeed);
  }

  // 2. X25519 KEM
  const wrappedArr = new Uint8Array(wrappedSeed);
  
  // Extract components
  const ephemeralPubRaw = wrappedArr.slice(0, 32);
  const iv = wrappedArr.slice(32, 44);
  const encryptedData = wrappedArr.slice(44);

  // Import ephemeral public key
  const ephemeralPublicKey = await crypto.subtle.importKey(
    'raw',
    ephemeralPubRaw,
    { name: 'X25519' },
    true,
    []
  );

  // Derive shared secret
  const sharedSecret = await crypto.subtle.deriveKey(
    { name: 'X25519', public: ephemeralPublicKey },
    devicePrivateKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );

  // Decrypt the seed
  return await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    sharedSecret,
    encryptedData
  );
}

/**
 * Derive epoch-specific key from workspace seed using HKDF
 *
 * Key derivation: DEK_epoch = HKDF(seed, salt=epoch_number, info="workspace-dek")
 */
export async function deriveEpochKey(
  workspaceId: string,
  seed: ArrayBuffer,
  epoch: number
): Promise<WorkspaceEpochKey> {
  // Import seed as HKDF base key
  const baseKey = await crypto.subtle.importKey(
    'raw',
    seed,
    { name: 'HKDF' },
    false,
    ['deriveKey']
  );

  // Use epoch number as salt
  const salt = new Uint8Array(4);
  new DataView(salt.buffer).setUint32(0, epoch, false); // Big-endian

  // Derive AES-GCM key for this epoch
  const epochKey = await crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt,
      info: new TextEncoder().encode('workspace-dek'),
    },
    baseKey,
    {
      name: 'AES-GCM',
      length: 256,
    },
    false, // not extractable (for security)
    ['encrypt', 'decrypt']
  );

  return {
    workspaceId,
    epoch,
    key: epochKey,
    algorithm: 'AES-GCM-256',
  };
}

/**
 * Encrypt Yjs document snapshot
 */
export async function encryptDocument(
  documentId: string,
  workspaceId: string,
  data: Uint8Array,
  epochKey: WorkspaceEpochKey,
  version: number
): Promise<EncryptedDocument> {
  const iv = crypto.getRandomValues(new Uint8Array(12)); // GCM nonce

  // Ensure data is ArrayBuffer-backed (not SharedArrayBuffer) for crypto.subtle
  const dataBuffer = new Uint8Array(data).buffer;

  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    epochKey.key,
    dataBuffer
  );

  return {
    documentId,
    workspaceId,
    epoch: epochKey.epoch,
    iv: arrayBufferToBase64(iv.buffer),
    encryptedData: arrayBufferToBase64(encrypted),
    algorithm: 'AES-GCM-256',
    version,
  };
}

/**
 * Decrypt Yjs document snapshot
 */
export async function decryptDocument(
  encrypted: EncryptedDocument,
  epochKey: WorkspaceEpochKey
): Promise<Uint8Array> {
  const iv = base64ToArrayBuffer(encrypted.iv);
  const encryptedData = base64ToArrayBuffer(encrypted.encryptedData);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(iv),
    },
    epochKey.key,
    encryptedData
  );

  return new Uint8Array(decrypted);
}

/**
 * Encrypt Yjs update
 */
export async function encryptUpdate(
  documentId: string,
  update: Uint8Array,
  epochKey: WorkspaceEpochKey,
  version: number,
  profileId: string
): Promise<EncryptedUpdate> {
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Ensure update is ArrayBuffer-backed
  const updateBuffer = new Uint8Array(update).buffer;

  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    epochKey.key,
    updateBuffer
  );

  return {
    documentId,
    epoch: epochKey.epoch,
    iv: arrayBufferToBase64(iv.buffer),
    encryptedUpdate: arrayBufferToBase64(encrypted),
    algorithm: 'AES-GCM-256',
    version,
    createdByProfileId: profileId,
  };
}

/**
 * Decrypt Yjs update
 */
export async function decryptUpdate(
  encrypted: EncryptedUpdate,
  epochKey: WorkspaceEpochKey
): Promise<Uint8Array> {
  const iv = base64ToArrayBuffer(encrypted.iv);
  const encryptedUpdate = base64ToArrayBuffer(encrypted.encryptedUpdate);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(iv),
    },
    epochKey.key,
    encryptedUpdate
  );

  return new Uint8Array(decrypted);
}

// Utility functions

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Export device public key (X25519 raw 32 bytes)
 */
export async function exportDevicePublicKey(publicKey: CryptoKey): Promise<string> {
  const format = publicKey.algorithm.name === 'RSA-OAEP' ? 'spki' : 'raw';
  const exported = await crypto.subtle.exportKey(format, publicKey);
  return arrayBufferToBase64(exported);
}

/**
 * Import device public key (X25519 raw 32 bytes or SPKI for legacy)
 */
export async function importDevicePublicKey(base64: string): Promise<CryptoKey> {
  const buffer = base64ToArrayBuffer(base64);
  
  // Try raw first (X25519)
  if (buffer.byteLength === 32) {
    return await crypto.subtle.importKey(
      'raw',
      buffer,
      { name: 'X25519' },
      true,
      []
    );
  }

  // Fallback to RSA-OAEP (SPKI)
  return await crypto.subtle.importKey(
    'spki',
    buffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    true,
    ['encrypt']
  );
}

/**
 * Encrypt a confidential string using AES-GCM-256 derived from workspace seed
 */
export async function encryptSecretString(
  plaintext: string,
  seed: ArrayBuffer
): Promise<{ ciphertext: string; iv: string }> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    seed,
    { name: 'HKDF' },
    false,
    ['deriveKey']
  );

  const key = await crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(4),
      info: new TextEncoder().encode('yada-account-secrets-v1'),
    },
    baseKey,
    {
      name: 'AES-GCM',
      length: 256,
    },
    false,
    ['encrypt', 'decrypt']
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);

  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoded
  );

  return {
    ciphertext: arrayBufferToBase64(encrypted),
    iv: arrayBufferToBase64(iv.buffer),
  };
}

/**
 * Decrypt a confidential string using AES-GCM-256 derived from workspace seed
 */
export async function decryptSecretString(
  envelope: { ciphertext: string; iv: string },
  seed: ArrayBuffer
): Promise<string> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    seed,
    { name: 'HKDF' },
    false,
    ['deriveKey']
  );

  const key = await crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(4),
      info: new TextEncoder().encode('yada-account-secrets-v1'),
    },
    baseKey,
    {
      name: 'AES-GCM',
      length: 256,
    },
    false,
    ['encrypt', 'decrypt']
  );

  const iv = base64ToArrayBuffer(envelope.iv);
  const data = base64ToArrayBuffer(envelope.ciphertext);

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(iv) },
    key,
    data
  );

  return new TextDecoder().decode(decrypted);
}
